import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FullScreenLoading, HelmetHead } from '~/components/common';
import { useGetGameResultQuery } from '~/features/missionGame';
import { PAGE_ROUTE } from '~/constants';
import { breakpoint } from '~/styles/themes';
import Diamond from '~/assets/img/icons/diamond.png';
import { Button } from './main';

const DIAMOND_COUNT = {
  true: 3,
  normal: 2,
  bad: 1,
};
export default function MissionResultPage() {
  return <MissionResultLayout />;
}

function MissionResultLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { answers } = location.state;

  const { data: gameResultData, isPending: isGetResultPending } =
    useGetGameResultQuery(answers);

  const renderEndingTitle = ({ type }: { type: string }) => {
    switch (type) {
      case 'true':
        return 'True Ending';
      case 'normal':
        return 'Normal Ending';
      case 'bad':
        return 'Bad Ending';
    }
  };

  const renderDiamonds = ({ type }: { type: string }) => {
    return (
      <DiamondContainer>
        {Array.from({
          length: DIAMOND_COUNT[type as keyof typeof DIAMOND_COUNT],
        }).map((_, index) => (
          <img src={Diamond} alt='diamond' key={index} width={22} height={19} />
        ))}
      </DiamondContainer>
    );
  };

  if (isGetResultPending) return <FullScreenLoading />;
  if (!gameResultData) return <div>Error loading game result.</div>;

  return (
    <>
      <HelmetHead title='Mission Game' />

      <Container>
        <ImageBackground
          src={gameResultData.image_file_path}
          alt='game background'
        />
        <BlurContainer>
          <BackgroundContainer />
        </BlurContainer>

        <GameIntroContainer>
          {renderDiamonds({ type: gameResultData.type })}
          <Title>{renderEndingTitle({ type: gameResultData.type })}</Title>
          <Intro>{gameResultData.story}</Intro>
          <ButtonContainer>
            <Button
              type='dark'
              text='Done'
              handleClick={() =>
                navigate(`${PAGE_ROUTE.MISSION_GAME}/${answers.game_id}`, {
                  state: { isMissionCompleted: true },
                })
              }
            />
          </ButtonContainer>
        </GameIntroContainer>
      </Container>
    </>
  );
}

const BlurContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  flex-direction: column;
  justify-content: end;
  width: 100%;
  height: 60%;
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  -webkit-mask: linear-gradient(180deg, transparent, black 30%);
  gap: 10px;
`;
const ImageBackground = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  padding-bottom: 1px;
`;
export const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: ${breakpoint.mobile_large};
  height: 100%;
  margin: 0 auto;
`;

const Title = styled.div`
  font-size: 36px;
  font-weight: 500;
`;

const Intro = styled.div`
  height: 250px;
  margin: 5px 0 30px;
  padding: 35px 0;
  overflow-x: hidden;
  overflow-y: scroll;
  -webkit-mask: linear-gradient(
    180deg,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  font-size: 18px;
  font-weight: 500;
  -webkit-overflow-scrolling: touch;
  line-height: 160%;
  white-space: pre-line;
  ${({ theme }) => css`
    color: ${theme.content.content_light_2};
  `}
  ${(props) => props.theme.mixins.scroll.none}
`;

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 16px;
  background: linear-gradient(
    180deg,
    transparent,
    ${({ theme }) => theme.backgroundColor.bg_dark_4_alpha} 50%
  );
`;

const GameIntroContainer = styled.div`
  display: flex;
  position: absolute;
  z-index: 10;
  bottom: 0;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  min-height: 50%;
  padding: 0 24px 25px;
  gap: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DiamondContainer = styled.div`
  display: flex;
  gap: 6.5px;
`;
